/* Wings commerce enhancements: colour-scoped variant choice, gallery and sticky cart. */
(() => {
  document.documentElement.classList.add('js');
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Same canonical scale as scripts/sync-shopify-wings.mjs. Unknown sizes retain
  // the owner's Shopify order after letter sizes and ascending numeric sizes.
  const sizeRanks = new Map([
    ['XXS', '2XS', 'XXSMALL', '2XSMALL'], ['XS', 'XSMALL', 'EXTRASMALL'],
    ['S', 'SMALL', 'SM'], ['M', 'MEDIUM', 'MED'], ['L', 'LARGE', 'LG'],
    ['XL', 'XLARGE', 'EXTRALARGE', '1XL'], ['XXL', '2XL', 'XXLARGE', '2XLARGE'],
    ['XXXL', '3XL', 'XXXLARGE', '3XLARGE'], ['XXXXL', '4XL'], ['XXXXXL', '5XL'],
  ].flatMap((names, rank) => names.map((name) => [name, rank])));
  const sizeKey = (value) => {
    const normalized = String(value || '').toUpperCase().replace(/[\s._-]/g, '');
    if (sizeRanks.has(normalized)) return [0, sizeRanks.get(normalized)];
    return /^\d+(?:\.\d+)?$/.test(String(value).trim()) ? [1, Number(value)] : null;
  };
  const sortBySize = (items, getSize) => Array.from(items).map((item, index) => ({ item, index, key: sizeKey(getSize(item)) }))
    .sort((a, b) => a.key && b.key ? a.key[0] - b.key[0] || a.key[1] - b.key[1] || a.index - b.index : a.key ? -1 : b.key ? 1 : a.index - b.index)
    .map(({ item }) => item);
  window.WingsCommerce = { sortBySize };
  const quickForms = (scope = document) => {
    scope.querySelectorAll('[data-quick-form]').forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const select = form.querySelector('select[name="id"]');
      const submit = form.querySelector('[data-quick-submit]');
      const sizes = Array.from(form.querySelectorAll('[data-quick-variant]'));
      if (sizes.length) sizes[0].parentElement.append(...sortBySize(sizes, (button) => button.dataset.size));
      select.required = false;
      submit.disabled = true;
      const reset = () => {
        select.value = '';
        submit.disabled = true;
        form.querySelectorAll('[data-quick-variant]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      };
      form.closest('dialog')?.addEventListener('close', reset);
      form.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quick-variant]');
        if (!button || button.disabled) return;
        select.value = button.dataset.quickVariant;
        submit.disabled = !select.value;
        form.querySelectorAll('[data-quick-variant]').forEach((size) => size.setAttribute('aria-pressed', String(size === button)));
      });
      form.addEventListener('submit', (event) => {
        if (!select.value) {
          event.preventDefault();
          event.stopImmediatePropagation();
          form.querySelector('[data-quick-variant]:not(:disabled)')?.focus();
        }
      }, true);
    });
  };
  const initialiseProduct = (scope = document) => {
    scope.querySelectorAll('[data-product-view]').forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const variants = JSON.parse(root.querySelector('[data-product-variants]').textContent);
      if (!variants.length) return;
      const form = root.querySelector('[data-product-form]');
      const select = form.querySelector('select[name="id"]');
      select.required = false;
      const groups = Array.from(form.querySelectorAll('[data-option-group]'));
      const sizeGroup = groups.find((group) => Number(group.dataset.optionGroup) === Number(root.dataset.sizeIndex));
      const sizeButtons = Array.from(sizeGroup?.querySelectorAll('[data-option-position]') || []);
      if (sizeButtons.length) sizeButtons[0].parentElement.append(...sortBySize(sizeButtons, (button) => button.dataset.optionValue));
      const buttons = Array.from(form.querySelectorAll('[data-option-position]'));
      const colorIndex = Number(root.dataset.colorIndex);
      const initial = variants.find((variant) => String(variant.id) === root.dataset.selectedVariant) || variants[0];
      let choices = initial.options.slice();
      const colourVariants = (color) => variants.filter((variant) => colorIndex < 0 || variant.options[colorIndex] === color);
      // Like the reference, multi-size products always require a deliberate size choice.
      const firstColour = colourVariants(initial.options[colorIndex]);
      if (firstColour.length > 1) {
        choices = choices.map((value, index) => index === colorIndex || new Set(firstColour.map((variant) => variant.options[index])).size === 1 ? value : null);
      }
      let selected = null;
      const mainAdd = root.querySelector('[data-main-add]');
      const sticky = root.querySelector('[data-product-sticky]');
      const stickyAdd = root.querySelector('[data-sticky-add]');
      const error = form.querySelector('[data-form-error]');
      const gallery = root.querySelector('[data-product-gallery]');
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-gallery-image]'));
      const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      const thumbStrip = gallery.querySelector('[data-gallery-thumbnails]');
      const zoomDialog = gallery.querySelector('[data-gallery-dialog]');
      const zoomImage = gallery.querySelector('[data-zoom-image]');
      let permittedSlides = slides.filter((slide) => !slide.hidden);
      let currentImage = 0;
      let zoomOpener = null;
      let currentColor = choices[colorIndex];
      const desktop = matchMedia('(min-width: 1024px)');
      const galleryCount = gallery.querySelector('[data-gallery-count]');
      const number = new Intl.NumberFormat(document.documentElement.lang || 'en');
      const fillLabel = (pattern, values) => (pattern || '').replace(/\{(\w+)\}|\[\[(\w+)\]\]/g, (_, key, alternate) => values[key || alternate] ?? '');
      const labelZoom = (button) => {
        const alt = button.querySelector('img')?.getAttribute('alt') || '';
        if (!button.dataset.zoomLabel) button.dataset.zoomLabel = button.getAttribute('aria-label') || '';
        button.setAttribute('aria-label', desktop.matches ? [button.dataset.zoomLabel, alt].filter(Boolean).join(': ') : alt);
      };
      const updateZoom = () => {
        const source = permittedSlides[currentImage]?.querySelector('img');
        if (!source || !zoomDialog.open) return;
        const copy = source.cloneNode();
        copy.loading = 'eager';
        copy.sizes = '(min-width: 1024px) 56rem, 100vw';
        zoomImage.replaceChildren(copy);
      };
      const showImage = (index, { scroll = true, focus = false } = {}) => {
        if (!permittedSlides.length) return;
        currentImage = desktop.matches || zoomDialog.open ? (index + permittedSlides.length) % permittedSlides.length : Math.max(0, Math.min(index, permittedSlides.length - 1));
        const current = permittedSlides[currentImage];
        slides.forEach((slide) => slide.toggleAttribute('data-current', slide === current));
        thumbnails.forEach((thumb) => {
          const active = thumb.dataset.galleryThumb === current.dataset.galleryImage;
          if (active) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
          thumb.tabIndex = !desktop.matches || active ? 0 : -1;
          if (active && focus) thumb.focus({ preventScroll: true });
          if (active && thumbStrip && desktop.matches) {
            const rect = thumb.getBoundingClientRect();
            const bounds = thumbStrip.getBoundingClientRect();
            const delta = rect.left < bounds.left ? rect.left - bounds.left : rect.right > bounds.right ? rect.right - bounds.right : 0;
            if (delta) thumbStrip.scrollBy({ left: delta, behavior: reducedMotion() ? 'instant' : 'smooth' });
          }
        });
        if (scroll && !desktop.matches) {
          const rect = current.getBoundingClientRect();
          const bounds = track.getBoundingClientRect();
          track.scrollBy({ left: rect.left - bounds.left, behavior: reducedMotion() ? 'instant' : 'smooth' });
        }
        if (galleryCount) galleryCount.textContent = fillLabel(root.dataset.slideLabel || '{index} / {total}', { index: number.format(currentImage + 1), total: number.format(permittedSlides.length) });
        gallery.querySelectorAll('[data-gallery-step]').forEach((button) => {
          const atEdge = !desktop.matches && !zoomDialog.open && (Number(button.dataset.galleryStep) < 0 ? currentImage === 0 : currentImage === permittedSlides.length - 1);
          button.setAttribute('aria-disabled', String(atEdge || permittedSlides.length <= 1));
        });
        updateZoom();
      };
      const filterGallery = (color) => {
        const belongsToColor = (slide) => {
          const linked = variants.filter((variant) => String(variant.imageId) === slide.dataset.galleryImage);
          return colorIndex < 0 || linked.length === 0 || linked.some((variant) => variant.options[colorIndex] === color);
        };
        permittedSlides = slides.filter(belongsToColor);
        if (colorIndex >= 0) {
          // Own colour images first; only images with no colour binding follow.
          permittedSlides.sort((a, b) => {
            const bound = (slide) => variants.some((variant) => String(variant.imageId) === slide.dataset.galleryImage && variant.options[colorIndex] === color);
            return Number(bound(b)) - Number(bound(a));
          });
        }
        gallery.classList.toggle('product-gallery--long', permittedSlides.length > 12);
        gallery.classList.toggle('product-gallery--single', permittedSlides.length <= 1);
        const allowed = new Set(permittedSlides.map((slide) => slide.dataset.galleryImage));
        permittedSlides.forEach((slide, index) => {
          const values = { index: number.format(index + 1), total: number.format(permittedSlides.length), title: root.dataset.productTitle || '', color: color || '' };
          slide.setAttribute('aria-label', fillLabel(root.dataset.slideLabel, values));
          const thumb = thumbnails.find((item) => item.dataset.galleryThumb === slide.dataset.galleryImage);
          if (thumb) thumb.setAttribute('aria-label', fillLabel(root.dataset.showImageLabel, values));
          if (slide.hasAttribute('data-generated-alt')) slide.querySelector('img')?.setAttribute('alt', fillLabel(root.dataset.imageAltLabel, values).replace(/([,،])\s*[,،]/g, '$1'));
          const zoom = slide.querySelector('[data-gallery-zoom]');
          if (zoom) labelZoom(zoom);
        });
        slides.forEach((slide) => { slide.hidden = !allowed.has(slide.dataset.galleryImage); });
        thumbnails.forEach((thumb) => { thumb.hidden = !allowed.has(thumb.dataset.galleryThumb); });
        permittedSlides.forEach((slide) => track.append(slide));
        if (thumbStrip) permittedSlides.forEach((slide) => {
          const thumb = thumbnails.find((item) => item.dataset.galleryThumb === slide.dataset.galleryImage);
          if (thumb) thumbStrip.append(thumb);
        });
        gallery.querySelectorAll('[data-gallery-step]').forEach((button) => { button.disabled = permittedSlides.length <= 1; });
        showImage(0, { scroll: false });
        track.scrollTo({ left: 0, behavior: 'instant' });
      };
      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) showImage(permittedSlides.findIndex((slide) => slide.dataset.galleryImage === thumb.dataset.galleryThumb));
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow && arrow.getAttribute('aria-disabled') !== 'true') showImage(currentImage + Number(arrow.dataset.galleryStep));
        const zoom = event.target.closest('[data-gallery-zoom]');
        if (zoom && slides.length && desktop.matches) {
          const index = permittedSlides.indexOf(zoom.closest('[data-gallery-image]'));
          if (index >= 0) showImage(index, { scroll: false });
          zoomOpener = zoom;
          if (window.Wings?.openDialog) window.Wings.openDialog(zoomDialog, zoom);
          else zoomDialog.showModal();
          updateZoom();
        }
      });
      const imageKeys = (event) => {
        let index = currentImage;
        const rtl = document.documentElement.dir === 'rtl';
        if (event.key === 'ArrowRight') index += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') index += rtl ? 1 : -1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = permittedSlides.length - 1;
        else return;
        event.preventDefault();
        showImage(index, { focus: !zoomDialog.open });
      };
      thumbStrip?.addEventListener('keydown', imageKeys);
      zoomDialog.addEventListener('keydown', imageKeys);
      zoomDialog.addEventListener('close', () => zoomOpener?.focus({ preventScroll: true }));
      let scrolling = false;
      track.addEventListener('scroll', () => {
        if (desktop.matches || scrolling) return;
        scrolling = true;
        requestAnimationFrame(() => {
          const bounds = track.getBoundingClientRect();
          let closest = 0;
          let distance = Infinity;
          permittedSlides.forEach((slide, index) => {
            const difference = Math.abs(slide.getBoundingClientRect().left - bounds.left);
            if (difference < distance) { closest = index; distance = difference; }
          });
          showImage(closest, { scroll: false });
          scrolling = false;
        });
      }, { passive: true });
      const galleryBreakpoint = () => {
        gallery.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
          button.tabIndex = desktop.matches ? 0 : -1;
          button.setAttribute('role', desktop.matches ? 'button' : 'img');
          labelZoom(button);
        });
        showImage(currentImage, { scroll: false });
      };
      desktop.addEventListener('change', galleryBreakpoint);
      galleryBreakpoint();
      const update = ({ changeUrl = false } = {}) => {
        const color = choices[colorIndex];
        const scoped = colourVariants(color);
        selected = variants.find((variant) => variant.options.every((value, index) => choices[index] === value)) || null;
        const representative = selected || scoped.reduce((lowest, variant) => !lowest || variant.price < lowest.price ? variant : lowest, null) || initial;
        const unavailable = selected ? !selected.available : scoped.every((variant) => !variant.available);
        select.value = selected ? String(selected.id) : '';
        buttons.forEach((button) => {
          const position = Number(button.dataset.optionPosition);
          const value = button.dataset.optionValue;
          const checked = choices[position] === value;
          if (position === colorIndex) {
            button.setAttribute('aria-checked', String(checked));
            button.tabIndex = checked ? 0 : -1;
          } else button.setAttribute('aria-pressed', String(checked));
          // Colours stay inspectable when sold out; size and other unavailable choices are disabled.
          if (position !== colorIndex) button.disabled = !variants.some((variant) => variant.available && variant.options[position] === value && variant.options.every((item, index) => index === position || choices[index] === null || choices[index] === item));
        });
        groups.forEach((group) => {
          const index = Number(group.dataset.optionGroup);
          const label = group.querySelector('[data-selected-option]');
          if (label) {
            label.textContent = choices[index] ? `: ${choices[index]}` : '';
            label.lang = /\p{Script=Arabic}/u.test(choices[index] || '') ? 'ar' : 'en';
          }
        });
        mainAdd.disabled = unavailable;
        mainAdd.setAttribute('aria-disabled', String(unavailable));
        mainAdd.textContent = unavailable ? root.dataset.soldLabel : root.dataset.addLabel;
        if (stickyAdd) {
          stickyAdd.disabled = unavailable;
          stickyAdd.textContent = mainAdd.textContent;
        }
        const hasRange = !selected && scoped.some((variant) => variant.price > representative.price);
        const currentPriceText = hasRange ? representative.fromText : representative.priceText;
        root.querySelector('[data-current-price]').textContent = currentPriceText;
        root.dataset.currentPriceMinor = String(representative.price);
        root.dataset.currentPriceText = currentPriceText;
        root.dispatchEvent(new CustomEvent('wings:product-price', { bubbles: true, detail: { priceMinor: representative.price, priceText: currentPriceText } }));
        const compare = root.querySelector('[data-compare-price]');
        const saving = root.querySelector('[data-price-saving]');
        const onSale = representative.comparePrice > representative.price;
        compare.hidden = !onSale;
        compare.textContent = representative.compareText;
        saving.hidden = !onSale;
        saving.textContent = representative.savingText;
        root.querySelector('[data-product-price]').classList.toggle('product-price--sale', onSale);
        if (sticky) {
          sticky.querySelector('[data-sticky-price]').textContent = hasRange ? representative.fromText : representative.priceText;
          const stickyCompare = sticky.querySelector('[data-sticky-compare]');
          stickyCompare.hidden = !onSale;
          stickyCompare.textContent = representative.compareText;
        }
        if (color !== currentColor) { currentColor = color; filterGallery(color); }
        if (changeUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String((selected || scoped.find((variant) => variant.available) || representative).id));
          history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
        }
      };
      form.addEventListener('click', (event) => {
        const button = event.target.closest('[data-option-position]');
        if (!button || button.disabled) return;
        const position = Number(button.dataset.optionPosition);
        choices[position] = button.dataset.optionValue;
        if (position === colorIndex) {
          const scoped = colourVariants(choices[colorIndex]);
          choices = choices.map((value, index) => {
            if (index === colorIndex) return value;
            const values = new Set(scoped.map((variant) => variant.options[index]));
            if (values.size === 1) return scoped[0].options[index];
            return scoped.some((variant) => variant.options[index] === value && variant.available) ? value : null;
          });
        }
        error.hidden = true;
        update({ changeUrl: true });
      });
      form.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-option-position]');
        if (!button) return;
        if (Number(button.dataset.optionPosition) === Number(root.dataset.sizeIndex)) {
          const sizes = Array.from(sizeGroup.querySelectorAll('[data-option-position]')).filter((item) => !item.disabled);
          let next = sizes.indexOf(button);
          const rtl = document.documentElement.dir === 'rtl';
          if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
          else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
          else if (event.key === 'ArrowDown') next += 1;
          else if (event.key === 'ArrowUp') next -= 1;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = sizes.length - 1;
          else return;
          if (!sizes.length) return;
          event.preventDefault();
          const target = sizes[(next + sizes.length) % sizes.length];
          target.click();
          target.focus({ preventScroll: true });
          return;
        }
        if (Number(button.dataset.optionPosition) !== colorIndex) return;
        const colours = buttons.filter((item) => Number(item.dataset.optionPosition) === colorIndex);
        let next = colours.indexOf(button);
        const rtl = document.documentElement.dir === 'rtl';
        if (event.key === 'ArrowRight') next += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') next += rtl ? 1 : -1;
        else if (event.key === 'ArrowDown') next += 1;
        else if (event.key === 'ArrowUp') next -= 1;
        else return;
        event.preventDefault();
        const target = colours[(next + colours.length) % colours.length];
        target.click();
        target.focus({ preventScroll: true });
      });
      const focusMissingChoice = () => {
        const group = groups.find((item) => choices[Number(item.dataset.optionGroup)] === null);
        error.textContent = root.dataset.selectLabel;
        error.hidden = false;
        if (group) {
          group.focus({ preventScroll: true });
          group.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'instant' : 'smooth' });
        }
      };
      form.addEventListener('submit', (event) => {
        if (!selected || !selected.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
          focusMissingChoice();
        }
      }, true);
      stickyAdd?.addEventListener('click', () => {
        if (!selected) focusMissingChoice();
        else if (selected.available) form.requestSubmit(mainAdd);
      });
      if (sticky) {
        const spacer = document.createElement('div');
        spacer.className = 'product-sticky-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        document.body.append(spacer);
        let pastButton = false;
        const stickyVisibility = () => {
          sticky.hidden = Boolean(document.querySelector('dialog[open]')) || (desktop.matches && !pastButton);
        };
        const intersection = new IntersectionObserver(([entry]) => {
          pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyVisibility();
        });
        intersection.observe(mainAdd);
        const modalObserver = new MutationObserver(stickyVisibility);
        modalObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
        desktop.addEventListener('change', stickyVisibility);
        document.addEventListener('focusin', (event) => {
          const element = event.target;
          if (sticky.hidden || !(element instanceof HTMLElement) || sticky.contains(element) || element.closest('dialog')) return;
          // iOS Safari focuses a tall main element on tap. Only clear keyboard focus.
          if (!element.matches(':focus-visible') || element.id === 'main' || element.id === 'MainContent') return;
          const bounds = element.getBoundingClientRect();
          if (bounds.height > innerHeight / 2) return;
          const top = sticky.getBoundingClientRect().top;
          if (bounds.bottom > top && bounds.top < innerHeight) window.scrollBy({ top: bounds.bottom - top + 16, behavior: 'instant' });
        });
        stickyVisibility();
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(root)) { intersection.disconnect(); modalObserver.disconnect(); spacer.remove(); }
        });
      }
      filterGallery(currentColor);
      update();
    });
  };
  quickForms();
  initialiseProduct();
  document.addEventListener('shopify:section:load', (event) => { quickForms(event.target); initialiseProduct(event.target); });
})();
